import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { Toast, Field, PageHeader, ProductSearch } from './v2-utils';
import '../styles/v2.css';

export default function AddInventoryV2() {
  const navigate  = useNavigate();
  const location  = useLocation();

  // Support pre-fill from URL params (Add Product → Add Unit → Stock Entry flow)
  const params           = new URLSearchParams(location.search);
  const productIdFromUrl = params.get('product_id');
  const productNameFromUrl = params.get('product_name') || '';

  // Product search state
  const [productName, setProductName]       = useState('');
  const [productId, setProductId]           = useState('');
  const [isProductSelected, setIsProductSelected] = useState(false);
  const [searchResults, setSearchResults]   = useState([]);
  const [isOnline, setIsOnline]             = useState(navigator.onLine);

  // Form state
  const [units, setUnits]                   = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [currentStock, setCurrentStock]     = useState('');
  const [stockLimit, setStockLimit]         = useState('');
  const [targetStock, setTargetStock]       = useState('');

  // UI state
  const [loading, setLoading]               = useState(false);
  const [submitting, setSubmitting]         = useState(false);
  const [toast, setToast]                   = useState(null);

  const justSelectedRef = useRef(false);
  const blurTimeoutRef  = useRef(null);

  useEffect(() => {
    const up = () => setIsOnline(true), down = () => setIsOnline(false);
    window.addEventListener('online', up); window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up); window.removeEventListener('offline', down);
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  // If arriving with product_id in URL, load it automatically
  useEffect(() => {
    if (!productIdFromUrl) return;
    setLoading(true);
    axiosInstance.get(`/products/${productIdFromUrl}`)
      .then(r => {
        const p = r.data;
        const display = formatDisplay(p);
        setProductId(String(productIdFromUrl));
        setProductName(display);
        setIsProductSelected(true);
        return axiosInstance.get(`/units/product/${productIdFromUrl}/unitInfo`);
      })
      .then(r => {
        const arr = Array.isArray(r.data) ? r.data : r.data.units || [];
        setUnits(arr);
        if (arr.length === 1) setSelectedUnitId(String(arr[0].unit_id));
      })
      .catch(() => setToast({ message: 'Failed to load product data.', severity: 'error' }))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIdFromUrl]);

  // Product search
  useEffect(() => {
    if (productName.length > 2 && isOnline && !justSelectedRef.current && !isProductSelected) {
      axiosInstance.get(`/products/search?q=${encodeURIComponent(productName)}`)
        .then(r => {
          const unique = Object.values(r.data.reduce((acc, p) => {
            const k = `${p.product_name}-${p.variety || ''}-${p.brand || ''}-${p.size || ''}`;
            if (!acc[k]) acc[k] = p;
            return acc;
          }, {}));
          setSearchResults(unique);
        })
        .catch(() => setSearchResults([]));
    } else {
      setSearchResults([]);
    }
    if (justSelectedRef.current) justSelectedRef.current = false;
  }, [productName, isOnline, isProductSelected]);

  // Auto-calculate stock limit as 10% of current stock
  useEffect(() => {
    const val = parseFloat(currentStock);
    if (!isNaN(val) && val > 0) {
      setStockLimit(Math.max(1, Math.ceil(val * 0.1)).toString());
    }
  }, [currentStock]);

  const formatDisplay = (p) => {
    let s = p.product_name;
    if (p.brand) s += ` (${p.brand})`;
    if (p.variety) s += ` - ${p.variety}`;
    return s;
  };

  const handleSelect = async (product) => {
    if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null; }
    justSelectedRef.current = true;
    const display = formatDisplay(product);
    setProductName(display);
    setProductId(String(product.product_id));
    setIsProductSelected(true);
    setSearchResults([]);
    setSelectedUnitId('');
    setUnits([]);
    setCurrentStock('');
    setStockLimit('');
    setTargetStock('');

    try {
      const r = await axiosInstance.get(`/units/product/${product.product_id}/unitInfo`);
      const arr = Array.isArray(r.data) ? r.data : r.data.units || [];
      setUnits(arr);
      if (arr.length === 1) setSelectedUnitId(String(arr[0].unit_id));
    } catch {}
  };

  const handleProductNameChange = (val) => {
    setProductName(val);
    setProductId('');
    setIsProductSelected(false);
    setUnits([]);
    setSelectedUnitId('');
    setCurrentStock('');
    setStockLimit('');
    setTargetStock('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productId) { setToast({ message: 'Please select a product.', severity: 'error' }); return; }
    if (!selectedUnitId) { setToast({ message: 'Please select a unit.', severity: 'error' }); return; }
    const stockVal = parseFloat(currentStock);
    if (isNaN(stockVal) || stockVal <= 0) { setToast({ message: 'Current stock must be greater than 0.', severity: 'error' }); return; }
    const limitVal = parseFloat(stockLimit);
    if (isNaN(limitVal) || limitVal < 0) { setToast({ message: 'Low stock threshold must be 0 or greater.', severity: 'error' }); return; }

    setSubmitting(true);
    try {
      await axiosInstance.post('/inventories', {
        product_id:    productId,
        current_stock: currentStock,
        stock_limit:   stockLimit,
        unit_id:       parseInt(selectedUnitId),
        ...(targetStock ? { target_stock: targetStock } : {}),
      });
      setToast({ message: 'Initial stock set successfully!', severity: 'success' });
      // Reset form for next entry
      setProductName('');
      setProductId('');
      setIsProductSelected(false);
      setUnits([]);
      setSelectedUnitId('');
      setCurrentStock('');
      setStockLimit('');
      setTargetStock('');
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Failed to set inventory.', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const selectedUnit = units.find(u => String(u.unit_id) === String(selectedUnitId));

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <PageHeader
        title="Add Stock"
        subtitle="Set the opening inventory for a product"
        onBack={() => navigate('/new/dashboard/inventories/view')}
      />

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Product search card */}
          <div className="v2-card" style={{ padding: 24 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 16px' }}>
              Product
            </p>
            <Field label="Search Product" hint="Type at least 3 characters to search">
              <ProductSearch
                value={productName}
                onChange={handleProductNameChange}
                onSelect={handleSelect}
                onBlur={() => { blurTimeoutRef.current = setTimeout(() => setSearchResults([]), 150); }}
                onFocus={() => { if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null; } }}
                results={isProductSelected ? [] : searchResults}
                placeholder="Search by product name…"
              />
              {isProductSelected && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg style={{ width: 10, height: 10, color: 'white' }} fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                  </div>
                  <span style={{ fontSize: 12, color: '#065f46', fontWeight: 500 }}>Product selected</span>
                  <button type="button" onClick={() => handleProductNameChange('')}
                    style={{ marginLeft: 4, fontSize: 12, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                    Change
                  </button>
                </div>
              )}
            </Field>

            {loading && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #e2e8f0', borderTopColor: '#10b981', animation: 'spin 0.8s linear infinite' }} />
                <span style={{ fontSize: 12, color: '#94a3b8' }}>Loading product data…</span>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
          </div>

          {/* Stock details card — only shown after product selected */}
          {isProductSelected && (
            <div className="v2-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>
                Stock Details
              </p>

              {units.length === 0 ? (
                <div style={{ padding: '14px 16px', borderRadius: 12, background: '#fef3c7', border: '1px solid #fde68a' }}>
                  <p style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: '#92400e' }}>
                    No units found for this product.
                  </p>
                  <p style={{ margin: '0 0 10px', fontSize: 12, color: '#92400e' }}>
                    You need to add buying and selling units before setting stock.
                  </p>
                  <button type="button"
                    onClick={() => navigate(`/new/dashboard/units/add?product_id=${productId}&from_product=true`)}
                    style={{ fontSize: 13, color: '#92400e', fontWeight: 700, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    Add units for this product →
                  </button>
                </div>
              ) : (
                <>
                  <Field label="Unit *">
                    <div style={{ position: 'relative' }}>
                      <select className="v2-input" style={{ paddingRight: 36, appearance: 'none', cursor: 'pointer' }}
                        value={selectedUnitId} onChange={e => setSelectedUnitId(e.target.value)} required>
                        <option value="">Select unit…</option>
                        {units.map(u => (
                          <option key={u.unit_id} value={String(u.unit_id)}>
                            {u.unit_type} ({u.unit_category})
                          </option>
                        ))}
                      </select>
                      <svg style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8', pointerEvents: 'none' }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                      </svg>
                    </div>
                  </Field>

                  <Field
                    label={selectedUnit ? `Current Stock (${selectedUnit.unit_type}) *` : 'Current Stock *'}
                    hint="Enter the physical count you have right now"
                  >
                    <input className="v2-input" type="number" placeholder="0" value={currentStock}
                      onChange={e => setCurrentStock(e.target.value)} min="0.01" step="0.01" required />
                  </Field>

                  <Field
                    label="Low Stock Threshold *"
                    hint="Auto-calculated as 10% of current stock. You'll be alerted when stock drops below this."
                  >
                    <input className="v2-input" type="number" placeholder="0" value={stockLimit}
                      onChange={e => setStockLimit(e.target.value)} min="0" step="0.01" required />
                  </Field>

                  <Field label="Target Stock" hint="Optional — your ideal maximum stock level">
                    <input className="v2-input" type="number" placeholder="Optional" value={targetStock}
                      onChange={e => setTargetStock(e.target.value)} min="0" step="0.01" />
                  </Field>
                </>
              )}
            </div>
          )}

          {/* Submit */}
          {isProductSelected && units.length > 0 && (
            <button type="submit" className="v2-btn-submit" disabled={submitting} style={{ fontSize: 15 }}>
              {submitting ? 'Saving…' : 'Set Initial Stock'}
            </button>
          )}
        </div>
      </form>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
